import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { CircleHelp, Crop, FlipHorizontal2, PersonStanding, ScreenShareOff, UserPlus } from 'lucide-react';
import PageLayout from '@/components/shared/PageLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SelectClientDialog } from '@/components/select-client-dialog';
import { ClientsOnlyAppWall, useClientsOnlySession } from '@/components/authnz/ClientsOnlyAppWall';
import { useSettings } from '@/components/settings/lib/settings-context';
import { useClient } from '@/context/client-context';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { useScreenShare } from './lib/use-screen-share';
import { useProcessedStream } from './lib/use-processed-stream';
import { RemoteShareProvider, type RemoteTool } from './lib/remote-share-context';
import type { CropRect } from './lib/frame-processing';
import RemoteToolPanel from './RemoteToolPanel';
import RemoteCropEditor from './RemoteCropEditor';
import { RemoteLiveGuide, RemoteShareSetup } from './RemoteAssessmentGuide';

function NoticeCard({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <Card className="max-w-lg w-full">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {children ? <CardDescription>{children}</CardDescription> : null}
      </CardHeader>
    </Card>
  );
}

function ClientGate() {
  const [open, setOpen] = useState(false);
  return (
    <Card className="max-w-lg w-full">
      <CardHeader>
        <CardTitle>Select a Client to Begin</CardTitle>
        <CardDescription>
          Pick the person you are about to call. Next, join the video call and share that window.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col sm:flex-row gap-3">
        <SelectClientDialog open={open} onOpenChange={setOpen}>
          <Button className="h-12" onClick={() => setOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Select Existing Client
          </Button>
        </SelectClientDialog>
        <Button asChild variant="secondary" className="h-12">
          <Link to="/clients/new">Create New Client</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * Remote Assessment workbench (VGon 3 layout): the practitioner shares their call window once;
 * the shared window fills the left pane, controls sit on the right, and the top bar switches
 * Posture Analysis (live landmarks) | SOP Assessment (VGon goniometer) on the same share. Mirror and crop apply once at the source.
 * Desktop only, behind experimental features. The selected client lives in the app header.
 */
export default function RemoteAssessmentPage() {
  const clientsOnly = useClientsOnlySession();
  const { settings, setSettings } = useSettings();
  // The 'what to do now' steps: a setting (Settings > Tooltips & Help), closable with X, back via ?.
  const stepsOn = settings.showRemoteAssessmentSteps !== false;
  const setStepsOn = (on: boolean) => setSettings((prev) => ({ ...prev, showRemoteAssessmentSteps: on }));
  const { client } = useClient();
  const isMobile = useIsMobile();
  const share = useScreenShare();
  const [tool, setTool] = useState<RemoteTool>('landmarks');
  const [mirror, setMirror] = useState(false);
  // What each tab saves to, shown in the top bar. Posture always starts a new analysis (Review &
  // save); SOP starts new and, after its first save, names the assessment it keeps updating.
  const [saveTargets, setSaveTargets] = useState<Record<RemoteTool, string>>({
    landmarks: 'New Posture Analysis',
    goniometer: 'New SOP Assessment (active)',
  });
  const reportSaveTarget = useCallback((t: RemoteTool, label: string) => {
    setSaveTargets((prev) => (prev[t] === label ? prev : { ...prev, [t]: label }));
  }, []);
  const [showLandmarks, setShowLandmarks] = useState(true);
  const [crop, setCrop] = useState<CropRect | null>(null);
  const [cropEditing, setCropEditing] = useState(false);

  const liveStream = share.status === 'live' ? share.stream : null;
  const toolStream = useProcessedStream(liveStream, { mirror, crop });

  if (clientsOnly) {
    return (
      <PageLayout title="Remote Assessment" compactContent>
        <ClientsOnlyAppWall />
      </PageLayout>
    );
  }

  let body: React.ReactNode;
  if (!settings.experimentalFeaturesEnabled) {
    body = (
      <NoticeCard title="Remote Assessment is an experimental feature">
        Turn on experimental features in <Link to="/settings" className="underline">Settings</Link> to use it.
      </NoticeCard>
    );
  } else if (isMobile) {
    body = (
      <Card className="max-w-lg w-full">
        <CardHeader>
          <CardTitle>Remote Assessment needs a desktop browser</CardTitle>
          <CardDescription>
            Screen sharing needs a desktop browser. Phones and tablets cannot share a call window into Harry.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 sm:flex-row">
          <Button asChild variant="secondary" className="h-11">
            <Link to="/posture-analysis">Posture Analysis</Link>
          </Button>
          <Button asChild variant="secondary" className="h-11">
            <Link to="/sop-assessment">SOP Assessment</Link>
          </Button>
        </CardContent>
      </Card>
    );
  } else if (share.status === 'unsupported') {
    body = (
      <NoticeCard title="This browser cannot share a screen or window">
        Use a current desktop Chrome, Edge, or Firefox.
      </NoticeCard>
    );
  } else if (!client) {
    body = <ClientGate />;
  } else if (!liveStream) {
    body = <RemoteShareSetup status={share.status} onShare={() => void share.start()} />;
  } else {
    body = (
      <RemoteShareProvider value={{ stream: toolStream, tool, showLandmarks, reportSaveTarget }}>
        <div className="flex h-[calc(100dvh-10rem)] min-h-[480px] flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Tabs value={tool} onValueChange={(v) => setTool(v as RemoteTool)}>
              <TabsList>
                <TabsTrigger value="landmarks">Posture Analysis</TabsTrigger>
                <TabsTrigger value="goniometer">SOP Assessment</TabsTrigger>
              </TabsList>
            </Tabs>
            <p className="min-w-0 flex-1 truncate text-sm text-muted-foreground" data-testid="remote-save-target">
              Saving to: <span className="font-medium text-foreground">{saveTargets[tool]}</span>
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                aria-label="Steps"
                aria-pressed={stepsOn}
                title={stepsOn ? 'Hide the steps' : 'Show the steps'}
                className={cn(stepsOn && 'border-primary text-primary')}
                onClick={() => setStepsOn(!stepsOn)}
              >
                <CircleHelp className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                aria-label="Landmarks overlay"
                aria-pressed={showLandmarks}
                title={showLandmarks ? 'Hide the body landmarks drawn on the client' : 'Show the body landmarks drawn on the client'}
                className={cn(showLandmarks && 'border-primary text-primary')}
                onClick={() => setShowLandmarks((v) => !v)}
              >
                <PersonStanding className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                aria-label="Mirror"
                aria-pressed={mirror}
                title="Mirror the picture if left and right look swapped"
                className={cn(mirror && 'border-primary text-primary')}
                onClick={() => setMirror((m) => !m)}
              >
                <FlipHorizontal2 className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                aria-label="Crop"
                aria-pressed={!!crop}
                title={crop ? 'Crop is on: draw again or clear it' : 'Crop: draw a box around the client if the window shows extra call controls'}
                className={cn(crop && 'border-primary text-primary')}
                onClick={() => setCropEditing(true)}
              >
                <Crop className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={share.stop}>
                <ScreenShareOff className="mr-2 h-4 w-4" />
                Stop sharing
              </Button>
            </div>
          </div>

          {stepsOn && <RemoteLiveGuide tool={tool} onClose={() => setStepsOn(false)} />}

          {cropEditing && (
            <RemoteCropEditor
              stream={liveStream}
              initialCrop={crop}
              onApply={(next) => {
                setCrop(next);
                setCropEditing(false);
              }}
              onClear={() => {
                setCrop(null);
                setCropEditing(false);
              }}
              onCancel={() => setCropEditing(false)}
            />
          )}
          {/* Tools stay mounted while cropping so unsaved work is kept. Keyed by client: switching
              client in the header starts fresh tools, so one client's unsaved stills and SoP values
              (or an already-created assessment id) can never be saved to another client. */}
          <div className={cn('min-h-0 flex-1', cropEditing && 'hidden')}>
            <RemoteToolPanel key={client.id} />
          </div>
        </div>
      </RemoteShareProvider>
    );
  }

  return (
    <PageLayout
      title="Remote Assessment"
      subtitle="Join a video call with the client, then share that window"
      compactContent
    >
      {body}
    </PageLayout>
  );
}
